import React from "react"
import { useState, useEffect } from "react"
import { Box, Flex, Card, Grid, Heading, Text, TextField, TextArea, Select, Button, Badge, Dialog, Separator } from "@radix-ui/themes"
import { StarIcon, MessageSquareIcon, ReplyIcon, FlagIcon } from "lucide-react"
import { toast } from "react-hot-toast"
import { feedbackAPI } from "../services/api"
import PageHeader from "../components/ui/dashboard/PageHeader"
import LoadingSpinner from "../components/ui/dashboard/LoadingSpinner"

interface Feedback {
  _id: string
  order: {
    _id: string
    orderNumber: string
  }
  customer: {
    _id: string
    username: string
    phoneNumber: string
  }
  restaurant: {
    _id: string
    name: string
  }
  captain?: {
    _id: string
    username: string
  }
  ratings: {
    food: number
    delivery: number
    service: number
    overall: number
  }
  comments: {
    food?: string
    delivery?: string
    general?: string
  }
  tags: string[]
  isPublic: boolean
  isVerified: boolean
  adminResponse?: {
    message: string
    respondedBy: {
      _id: string
      username: string
    }
    respondedAt: string
  }
  helpfulVotes: number
  reportCount: number
  status: "active" | "hidden" | "reported" | "flagged"
  createdAt: string
  updatedAt: string
}

export default function FeedbackPage() {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [filterRating, setFilterRating] = useState("all")
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false)
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null)
  const [responseMessage, setResponseMessage] = useState("")

  useEffect(() => {
    fetchFeedbacks()
  }, [filterStatus, filterRating])

  const fetchFeedbacks = async () => {
    try {
      setLoading(true)
      const params: any = {}
      if (filterStatus !== "all") params.status = filterStatus
      if (filterRating && filterRating !== "all") params.rating = filterRating

      const response = await feedbackAPI.getAllFeedback(params)
      setFeedbacks(response.data.feedbacks || [])
    } catch (error) {
      console.error("Error fetching feedback:", error)
      toast.error("Failed to load feedback")
    } finally {
      setLoading(false)
    }
  }

  const handleRespondToFeedback = async () => {
    if (!selectedFeedback || !responseMessage.trim()) {
      toast.error("Please enter a response message")
      return
    }

    try {
      await feedbackAPI.respondToFeedback(selectedFeedback._id, { message: responseMessage })
      toast.success("Response sent successfully!")
      setIsResponseModalOpen(false)
      setSelectedFeedback(null)
      setResponseMessage("")
      fetchFeedbacks()
    } catch (error) {
      console.error("Error responding to feedback:", error)
      toast.error("Failed to send response")
    }
  }

  const handleUpdateStatus = async (feedbackId: string, newStatus: string) => {
    try {
      await feedbackAPI.updateFeedbackStatus(feedbackId, newStatus)
      toast.success("Status updated successfully!")
      fetchFeedbacks()
    } catch (error) {
      console.error("Error updating status:", error)
      toast.error("Failed to update status")
    }
  }

  const renderStars = (rating: number) => {
    return (
      <Flex align="center" gap="1">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            key={star}
            className="w-4 h-4"
            style={{
              color: star <= rating ? "#facc15" : "#d1d5db",
              fill: star <= rating ? "#facc15" : "none"
            }}
          />
        ))}
        <Text size="2" color="gray">({rating})</Text>
      </Flex>
    )
  }

  const getStatusBadge = (status: string) => {
    const colorMap = {
      active: "green" as const,
      hidden: "gray" as const,
      reported: "red" as const,
      flagged: "red" as const,
    }

    return (
      <Badge color={colorMap[status as keyof typeof colorMap] || "gray"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const filteredFeedbacks = feedbacks.filter((feedback) =>
    feedback.customer.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    feedback.restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    feedback.order.orderNumber.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (loading) {
    return <LoadingSpinner size="3" text="Loading feedback..." />
  }

  return (
    <Flex direction="column" gap="6">
      <PageHeader
        title="Customer Feedback"
        description="Manage reviews and customer responses"
      />

      {/* Filters */}
      <Card>
        <Flex direction="column" gap="2">
          <Text size="2" weight="medium">Filters</Text>
          <Flex gap="3" wrap="wrap">
            <Box style={{ flex: "1", minWidth: "250px" }}>
              <TextField.Root
                placeholder="Search by customer, restaurant, or order number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>
            <Select.Root value={filterStatus} onValueChange={setFilterStatus}>
              <Select.Trigger placeholder="All Status" />
              <Select.Content>
                <Select.Item value="all">All Status</Select.Item>
                <Select.Item value="active">Active</Select.Item>
                <Select.Item value="hidden">Hidden</Select.Item>
                <Select.Item value="reported">Reported</Select.Item>
                <Select.Item value="flagged">Flagged</Select.Item>
              </Select.Content>
            </Select.Root>
            <Select.Root value={filterRating} onValueChange={setFilterRating}>
              <Select.Trigger placeholder="All Ratings" />
              <Select.Content>
                <Select.Item value="all">All Ratings</Select.Item>
                <Select.Item value="5">5 Stars</Select.Item>
                <Select.Item value="4">4+ Stars</Select.Item>
                <Select.Item value="3">3+ Stars</Select.Item>
                <Select.Item value="2">2+ Stars</Select.Item>
                <Select.Item value="1">1+ Stars</Select.Item>
              </Select.Content>
            </Select.Root>
          </Flex>
        </Flex>
      </Card>

      {/* Feedback Grid */}
      <Grid columns={{ initial: "1", lg: "2" }} gap="6">
        {filteredFeedbacks.map((feedback) => (
          <Card key={feedback._id}>
            <Flex direction="column" gap="4">
              {/* Header */}
              <Flex justify="between" align="start">
                <Box>
                  <Heading size="4">{feedback.customer.username}</Heading>
                  <Text size="2" color="gray">
                    Order #{feedback.order.orderNumber} • {feedback.restaurant.name}
                  </Text>
                </Box>
                {getStatusBadge(feedback.status)}
              </Flex>

              <Separator size="4" />

              {/* Content */}
              <Flex direction="column" gap="4">
                {/* Ratings */}
                <Flex direction="column" gap="2">
                  <Flex justify="between" align="center">
                    <Text size="2" weight="medium">Overall:</Text>
                    {renderStars(feedback.ratings.overall)}
                  </Flex>
                  <Flex justify="between" align="center">
                    <Text size="2">Food:</Text>
                    {renderStars(feedback.ratings.food)}
                  </Flex>
                  <Flex justify="between" align="center">
                    <Text size="2">Delivery:</Text>
                    {renderStars(feedback.ratings.delivery)}
                  </Flex>
                  <Flex justify="between" align="center">
                    <Text size="2">Service:</Text>
                    {renderStars(feedback.ratings.service)}
                  </Flex>
                </Flex>

                {/* Comments */}
                {feedback.comments.general && (
                  <Box>
                    <Text size="2" weight="medium">General Comment:</Text>
                    <Text size="2" color="gray">{feedback.comments.general}</Text>
                  </Box>
                )}

                {feedback.comments.food && (
                  <Box>
                    <Text size="2" weight="medium">Food Comment:</Text>
                    <Text size="2" color="gray">{feedback.comments.food}</Text>
                  </Box>
                )}

                {feedback.comments.delivery && (
                  <Box>
                    <Text size="2" weight="medium">Delivery Comment:</Text>
                    <Text size="2" color="gray">{feedback.comments.delivery}</Text>
                  </Box>
                )}

                {/* Tags */}
                {feedback.tags && feedback.tags.length > 0 && (
                  <Box>
                    <Text size="2" weight="medium" mb="1">Tags:</Text>
                    <Flex gap="1" wrap="wrap">
                      {feedback.tags.map((tag, index) => (
                        <Badge key={index} variant="soft" size="1">
                          {tag}
                        </Badge>
                      ))}
                    </Flex>
                  </Box>
                )}

                {/* Admin Response */}
                {feedback.adminResponse && (
                  <Box p="3" style={{ backgroundColor: "var(--blue-3)", borderRadius: "var(--radius-3)" }}>
                    <Text size="2" weight="medium">Admin Response:</Text>
                    <Text size="2">{feedback.adminResponse.message}</Text>
                    <Text size="1" color="gray" mt="1">
                      By {feedback.adminResponse.respondedBy.username} on{" "}
                      {new Date(feedback.adminResponse.respondedAt).toLocaleDateString()}
                    </Text>
                  </Box>
                )}

                {/* Stats */}
                <Flex justify="between">
                  <Text size="1" color="gray">Helpful: {feedback.helpfulVotes}</Text>
                  <Text size="1" color="gray">Reports: {feedback.reportCount}</Text>
                  <Text size="1" color="gray">{new Date(feedback.createdAt).toLocaleDateString()}</Text>
                </Flex>

                {/* Actions */}
                <Flex gap="2" pt="2" style={{ borderTop: "1px solid var(--gray-5)" }}>
                  <Button
                    variant="soft"
                    size="2"
                    style={{ flex: 1 }}
                    onClick={() => {
                      setSelectedFeedback(feedback)
                      setIsResponseModalOpen(true)
                    }}
                  >
                    <ReplyIcon className="w-3 h-3" />
                    Respond
                  </Button>
                  <Select.Root 
                    value={feedback.status} 
                    onValueChange={(value) => handleUpdateStatus(feedback._id, value)}
                  >
                    <Select.Trigger />
                    <Select.Content>
                      <Select.Item value="active">Active</Select.Item>
                      <Select.Item value="hidden">Hidden</Select.Item>
                      <Select.Item value="reported">Reported</Select.Item>
                      <Select.Item value="flagged">Flagged</Select.Item>
                    </Select.Content>
                  </Select.Root>
                </Flex>
              </Flex>
            </Flex>
          </Card>
        ))}
      </Grid>

      {!loading && filteredFeedbacks.length === 0 && (
        <Box style={{ textAlign: "center", padding: "var(--space-8)" }}>
          <Text color="gray">No feedback found</Text>
        </Box>
      )}

      {/* Response Modal */}
      <Dialog.Root open={isResponseModalOpen} onOpenChange={setIsResponseModalOpen}>
        <Dialog.Content style={{ maxWidth: 450 }}>
          <Dialog.Title>Respond to Feedback</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Send a response to {selectedFeedback?.customer.username}
          </Dialog.Description>

          <Flex direction="column" gap="4">
            <Box>
              <Text as="label" size="2" weight="medium" mb="1">
                Response Message
              </Text>
              <TextArea
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                placeholder="Enter your response..."
                rows={4}
              />
            </Box>

            <Flex gap="3" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button onClick={handleRespondToFeedback}>
                Send Response
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Flex>
  )
}